import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  ActivityIndicator, ScrollView, useWindowDimensions,
} from 'react-native';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import {
  getDeliveryPayroll, getMonthTransactions, recordPayout,
  DPPayrollRow, Txn, OrderLite, NIGHT_BONUS_PER_ORDER,
} from '../query/payrollService';
import {
  C, MONTHS, inr, txnDate, MIcon, RightDrawer, PayModal, FilterChips,
  StatusBadge, rowStatus, KV, ChipStat, sx,
} from './PayrollShared';

const PAY_METHODS = ['Bank Transfer', 'UPI', 'Cash', 'Other'];

export default function DeliveryPayroll() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [tab, setTab] = useState<'payroll' | 'ledger'>('payroll');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState<DPPayrollRow[]>([]);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [loading, setLoading] = useState(true);

  const [detail, setDetail] = useState<DPPayrollRow | null>(null);
  const [orderFilter, setOrderFilter] = useState(0); // 0 all, 1 late night
  const [pay, setPay] = useState<{ row: DPPayrollRow; full: boolean } | null>(null);
  const [editingSalary, setEditingSalary] = useState(false);
  const [salaryInput, setSalaryInput] = useState('');

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2600);
  };

  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([getDeliveryPayroll(month, year), getMonthTransactions(month, year)]);
      setRows(r);
      setTxns(t);
      // Keep the open drawer in sync after a payment.
      setDetail(prev => (prev ? r.find(x => x.entityId === prev.entityId) ?? null : null));
    } catch (e) {
      console.error('delivery payroll load failed', e);
      showToast('Failed to load payroll', 'error');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta: number) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth())) return;
    setMonth(m); setYear(y);
  };

  const summary = useMemo(() => {
    let outstanding = 0, paid = 0, paidCount = 0, dueCount = 0;
    rows.forEach(r => {
      outstanding += r.remaining;
      paid += r.paidSoFar;
      if (r.isPaid) paidCount++;
      else if (r.payable > 0) dueCount++;
    });
    return { outstanding, paid, paidCount, dueCount };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => r.name.toLowerCase().includes(q) || r.entityId.toLowerCase().includes(q));
  }, [rows, search]);

  const partnerTxns = useMemo(() => txns.filter(t => (t.type ?? 'restaurant') === 'partner'), [txns]);
  const filteredTxns = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return partnerTxns;
    return partnerTxns.filter(t => (t.entityName || '').toLowerCase().includes(q) || (t.referenceId || '').toLowerCase().includes(q));
  }, [partnerTxns, search]);

  const openDetail = (r: DPPayrollRow) => { setDetail(r); setOrderFilter(0); setEditingSalary(false); };

  const submitPay = async ({ amount, method, reference, notes }: { amount: number; method: string; reference: string; notes: string }) => {
    if (!pay) return;
    const r = pay.row;
    await recordPayout({
      type: 'partner', entityId: r.entityId, entityName: r.name, amount,
      gross: r.payable, deductions: 0, orderCount: r.deliveredCount,
      periodMonth: month, periodYear: year, referenceId: reference, paymentMethod: method, notes: notes || undefined,
    });
    setPay(null);
    showToast('Payout recorded');
    await load();
  };

  const saveSalary = async (r: DPPayrollRow) => {
    const v = Number(salaryInput) || 0;
    try {
      await setDoc(doc(db, 'zapee_delivery_partners', r.entityId), { salary: v }, { merge: true });
      setEditingSalary(false);
      showToast('Salary updated');
      await load();
    } catch (e) {
      showToast('Failed to update salary', 'error');
    }
  };

  const Th = ({ label, flex, align }: { label: string; flex: number; align?: 'center' | 'right' }) => (
    <View style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}>
      <Text style={tb.thTxt}>{label}</Text>
    </View>
  );

  const renderPartners = () => (
    <View style={tb.table}>
      <View style={tb.headerRow}>
        <Th label="Partner" flex={2.2} />
        <Th label="Delivered" flex={1} align="center" />
        <Th label="Salary" flex={1.1} align="right" />
        <Th label="Surge" flex={1} align="right" />
        <Th label="Night" flex={1} align="right" />
        <Th label="Payable" flex={1.2} align="right" />
        <Th label="Status" flex={1} align="center" />
        <Th label="" flex={1.1} align="right" />
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
        {filteredRows.length === 0 ? (
          <View style={sx.empty}><Text style={sx.emptyTxt}>No delivery partners</Text></View>
        ) : filteredRows.map((r, i) => (
          <TouchableOpacity key={r.entityId} style={[tb.row, i % 2 === 1 && tb.rowAlt]} onPress={() => openDetail(r)}>
            <View style={[tb.td, { flex: 2.2 }]}>
              <Text style={tb.name} numberOfLines={1}>{r.name}</Text>
              <Text style={tb.sub} numberOfLines={1}>{r.entityId}</Text>
            </View>
            <Text style={[tb.cell, { flex: 1, textAlign: 'center' }]}>{r.deliveredCount}</Text>
            <Text style={[tb.cell, { flex: 1.1, textAlign: 'right' }]}>{inr(r.salary)}</Text>
            <Text style={[tb.cell, { flex: 1, textAlign: 'right', color: C.accent }]}>{r.surge ? `+${inr(r.surge)}` : '—'}</Text>
            <Text style={[tb.cell, { flex: 1, textAlign: 'right', color: C.indigo }]}>{r.nightBonus ? `+${inr(r.nightBonus)}` : '—'}</Text>
            <Text style={[tb.cell, { flex: 1.2, textAlign: 'right', fontWeight: '800' }]}>{inr(r.payable)}</Text>
            <View style={[tb.td, { flex: 1, alignItems: 'center' }]}><StatusBadge status={rowStatus(r.isPaid, r.paidSoFar)} /></View>
            <View style={[tb.td, { flex: 1.1, alignItems: 'flex-end' }]}>
              {!r.isPaid && r.payable > 0 ? (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={sx.tapPayTxt}>Pay</Text>
                  <MIcon name="chevron_right" size={18} color={C.accent} />
                </View>
              ) : <Text style={tb.muted}>—</Text>}
            </View>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderLedger = () => (
    <View style={tb.table}>
      <View style={tb.headerRow}>
        <Th label="Partner" flex={2.2} />
        <Th label="Amount" flex={1.1} align="right" />
        <Th label="Method" flex={1.2} />
        <Th label="Reference" flex={1.4} />
        <Th label="Date" flex={1.5} align="right" />
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
        {filteredTxns.length === 0 ? (
          <View style={sx.empty}><Text style={sx.emptyTxt}>No payouts this month</Text></View>
        ) : filteredTxns.map((t, i) => (
          <View key={t.id} style={[tb.row, i % 2 === 1 && tb.rowAlt]}>
            <View style={[tb.td, { flex: 2.2 }]}>
              <Text style={tb.name} numberOfLines={1}>{t.entityName || t.entityId}</Text>
              <Text style={tb.sub} numberOfLines={1}>{t.entityId}</Text>
            </View>
            <Text style={[tb.cell, { flex: 1.1, textAlign: 'right', color: C.accent, fontWeight: '800' }]}>{inr(t.amount)}</Text>
            <Text style={[tb.cell, { flex: 1.2 }]} numberOfLines={1}>{t.paymentMethod || '—'}</Text>
            <Text style={[tb.cell, { flex: 1.4 }]} numberOfLines={1}>{t.referenceId || '—'}</Text>
            <Text style={[tb.cell, { flex: 1.5, textAlign: 'right' }]} numberOfLines={1}>{txnDate(t.paidAt)}</Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  // ── Drawer order list ──
  const drawerOrders = useMemo(() => {
    if (!detail) return [] as OrderLite[];
    const list = [...detail.orders].sort((a, b) => (b.deliveredAt?.getTime() ?? b.createdAt?.getTime() ?? 0) - (a.deliveredAt?.getTime() ?? a.createdAt?.getTime() ?? 0));
    return orderFilter === 1 ? list.filter(o => o.isNight) : list;
  }, [detail, orderFilter]);

  return (
    <View style={sx.screen}>
      <View style={sx.monthRow}>
        <TouchableOpacity style={sx.monthBtn} onPress={() => changeMonth(-1)}><Text style={sx.monthBtnTxt}>‹</Text></TouchableOpacity>
        <View style={sx.monthLabelWrap}><Text style={sx.monthLabel}>{MONTHS[month]} {year}</Text></View>
        <TouchableOpacity style={[sx.monthBtn, isCurrentMonth && sx.monthBtnDisabled]} onPress={() => changeMonth(1)} disabled={isCurrentMonth}>
          <Text style={[sx.monthBtnTxt, isCurrentMonth && sx.monthBtnTxtDisabled]}>›</Text>
        </TouchableOpacity>
      </View>

      <View style={sx.summaryCard}>
        <View style={[sx.summaryRow, isMobile && { flexWrap: 'wrap', gap: 16 }]}>
          <View style={sx.summaryItem}>
            <Text style={sx.summaryLabel}>Total Outstanding</Text>
            <Text style={sx.summaryValueBig}>{inr(summary.outstanding)}</Text>
            <Text style={sx.summaryCaption}>{summary.dueCount} due</Text>
          </View>
          <View style={sx.summaryDivider} />
          <View style={sx.summaryItem}>
            <Text style={sx.summaryLabel}>Paid This Month</Text>
            <Text style={sx.summaryValue}>{inr(summary.paid)}</Text>
            <Text style={sx.summaryCaption}>{summary.paidCount} fully paid</Text>
          </View>
          <View style={sx.summaryDivider} />
          <View style={sx.summaryItem}>
            <Text style={sx.summaryLabel}>Partners</Text>
            <Text style={sx.summaryValue}>{rows.length}</Text>
            <Text style={sx.summaryCaption}>{summary.paidCount} paid · {summary.dueCount} due</Text>
          </View>
        </View>
      </View>

      <View style={[sx.controls, isMobile && { flexDirection: 'column', alignItems: 'stretch', gap: 10 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 'auto' as any }}>
          <MIcon name="savings" size={20} color="#4ECDC4" />
          <View>
            <Text style={{ fontSize: 15, fontWeight: '800', color: '#fff' }}>Delivery Payroll</Text>
            <Text style={{ fontSize: 11, color: '#8BAAB8', marginTop: 1 }}>Rider earnings & payouts</Text>
          </View>
        </View>
        <View style={sx.tabRow}>
          {([['payroll', 'Payroll'], ['ledger', 'Ledger']] as ['payroll' | 'ledger', string][]).map(([t, lbl]) => (
            <TouchableOpacity key={t} style={[sx.tab, tab === t && sx.tabActive]} onPress={() => setTab(t)}>
              <Text style={[sx.tabTxt, tab === t && sx.tabTxtActive]}>{lbl}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={[sx.searchBox, { flex: 1 }]}>
          <MIcon name="search" size={15} color="#8BAAB8" style={{ marginRight: 6 }} />
          <TextInput style={sx.searchInput} placeholder="Search by name or id…" value={search} onChangeText={setSearch} placeholderTextColor="#8BAAB8" />
        </View>
        <TouchableOpacity style={sx.refreshBtn} onPress={load}><MIcon name="refresh" size={18} color="#4ECDC4" /></TouchableOpacity>
      </View>

      {loading ? (
        <View style={sx.loading}><ActivityIndicator size="large" color={C.teal} /><Text style={sx.loadingTxt}>Loading payroll…</Text></View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: 1000, flexGrow: 1 }}>
          {tab === 'payroll' ? renderPartners() : renderLedger()}
        </ScrollView>
      )}

      {/* Details drawer */}
      <RightDrawer visible={!!detail} onClose={() => setDetail(null)} title={detail?.name || ''} subtitle={`${MONTHS[month]} ${year} · Delivery payroll`}>
        {detail && (
          <>
            <View style={sx.drawerCard}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>Total Payable</Text>
                <Text style={{ fontSize: 22, fontWeight: '900', color: C.accent }}>{inr(detail.payable)}</Text>
              </View>
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 5 }}>
                <Text style={{ fontSize: 13, color: C.muted, fontWeight: '600' }}>Base Salary</Text>
                {editingSalary ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <TextInput
                      style={tb.salaryInput as any}
                      value={salaryInput}
                      onChangeText={(v) => setSalaryInput(v.replace(/[^0-9]/g, ''))}
                      keyboardType="numeric"
                      autoFocus
                    />
                    <TouchableOpacity style={tb.salarySave} onPress={() => saveSalary(detail)}><Text style={tb.salarySaveTxt}>Save</Text></TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }} onPress={() => { setSalaryInput(String(Math.round(detail.salary))); setEditingSalary(true); }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: C.text }}>{inr(detail.salary)}</Text>
                    <MIcon name="edit" size={14} color={C.muted} />
                  </TouchableOpacity>
                )}
              </View>
              <KV label="Surge (extra)" value={`+${inr(detail.surge)}`} color={C.accent} />
              <KV label={`Night Bonus (${detail.nightCount} × ${inr(NIGHT_BONUS_PER_ORDER)})`} value={`+${inr(detail.nightBonus)}`} color={C.indigo} />
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Paid So Far" value={inr(detail.paidSoFar)} color={C.accent} />
              <KV label="Remaining" value={inr(detail.remaining)} color={detail.remaining > 0 ? C.danger : C.muted} bold />
            </View>

            {!detail.isPaid && detail.payable > 0 && (
              <View style={sx.payBtnRow}>
                <TouchableOpacity style={sx.partialBtn} onPress={() => setPay({ row: detail, full: false })}><Text style={sx.partialTxt}>Pay Partial</Text></TouchableOpacity>
                <TouchableOpacity style={sx.fullBtn} onPress={() => setPay({ row: detail, full: true })}><Text style={sx.fullTxt}>Pay Full {inr(detail.remaining)}</Text></TouchableOpacity>
              </View>
            )}

            <Text style={sx.sectionLabel}>ORDER HISTORY · {detail.orders.length} DELIVERED · {detail.nightCount} LATE-NIGHT</Text>
            <FilterChips
              value={orderFilter}
              onChange={setOrderFilter}
              options={[{ label: 'All', value: 0, count: detail.orders.length }, { label: 'Late Night', value: 1, count: detail.nightCount }]}
            />
            {drawerOrders.length === 0 ? (
              <View style={[sx.orderCard, { alignItems: 'center', paddingVertical: 22 }]}><Text style={{ color: C.muted, fontSize: 12.5, fontWeight: '600' }}>No orders</Text></View>
            ) : drawerOrders.map(o => <OrderRow key={o.id} o={o} />)}
          </>
        )}
      </RightDrawer>

      <PayModal
        visible={!!pay}
        onClose={() => setPay(null)}
        title={pay?.full ? 'Pay Full' : 'Partial Payment'}
        entityName={pay?.row.name || ''}
        remaining={pay?.row.remaining || 0}
        full={!!pay?.full}
        methods={PAY_METHODS}
        onConfirm={submitPay}
      />

      {toast && (
        <View style={[styles.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.accent }]}>
          <Text style={styles.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

function OrderRow({ o }: { o: OrderLite }) {
  const t = o.createdAt;
  const time = t ? t.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <View style={sx.orderCard}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>#{o.id.slice(0, 6).toUpperCase()}</Text>
        {o.isNight && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: C.indigo + '22', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 7 }}>
            <MIcon name="nightlight_round" size={11} color={C.indigo} />
            <Text style={{ color: C.indigo, fontSize: 10.5, fontWeight: '900' }}>Night +{inr(NIGHT_BONUS_PER_ORDER)}</Text>
          </View>
        )}
      </View>
      {time ? <Text style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>Placed {time}</Text> : null}
      <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
      <Mini label="Order Total" value={inr(o.grandTotal)} color={C.muted} />
      {o.surge > 0 && <Mini label="Surge (partner earns)" value={`+${inr(o.surge)}`} color={C.accent} />}
      <Mini label="Payment" value={o.paymentMethod} color={C.muted} />
    </View>
  );
}

const Mini = ({ label, value, color }: { label: string; value: string; color: string }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 1.5 }}>
    <Text style={{ fontSize: 11, color: C.muted, fontWeight: '600' }}>{label}</Text>
    <Text style={{ fontSize: 11, fontWeight: '800', color }}>{value}</Text>
  </View>
);

const styles = StyleSheet.create({
  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#17201B', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#0A1712', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.muted, letterSpacing: 0.6, textTransform: 'uppercase' as const },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.09)' },
  rowAlt: { backgroundColor: '#1A2820' },
  td: { justifyContent: 'center', paddingRight: 8 },
  cell: { fontSize: 13, color: C.text, paddingRight: 8 },
  name: { fontSize: 14, fontWeight: '600', color: C.text },
  sub: { fontSize: 10, color: C.muted, marginTop: 1 },
  muted: { fontSize: 13, color: C.muted },
  salaryInput: { width: 90, height: 34, borderWidth: 1.5, borderColor: C.border, borderRadius: 8, paddingHorizontal: 8, fontSize: 13, color: C.text, outlineStyle: 'none' } as any,
  salarySave: { backgroundColor: C.accent, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7 },
  salarySaveTxt: { color: '#fff', fontWeight: '700', fontSize: 12 },
});
